/**
 * `MachineCard` — the demo's `machineCard(f)` (line 2131), as a component.
 *
 * One card per machine: id, condition band, model/drive/working, engine hours,
 * availability, the class key metric with its alarm limit, a 12-day sparkline
 * and the worst attention-band component. Clicking opens `/machines/<id>`.
 *
 * Used by `FleetHealthScene` (fleet health, six mine sections) and by
 * `MineSiteScene`'s "Fleet & condition" tab, which is the demo's own reuse —
 * `renderSites`' `fleet` tab calls `machineCard` too (line 3245).
 *
 * The top-right thumbnail is the model's turntable icon (`turnIcon(f)` →
 * `<span class="rpic">`, demo lines 1150–1153 and 2129), in the brand livery
 * and the drive tint, via `MachineThumb`.
 *
 * UI layer. Derivation lives in `FleetHealthScene.transform.ts` and
 * `shared/oem-engine.ts`; this file only renders.
 *
 * @see docs/logic/fleet/fleet-health.md
 */

import { Link } from 'react-router-dom';
import { cn } from '@/shared/cn';
import { WORKINGS } from '../../data/core';
import { CONDITION_BAND_LABEL } from '../../schema/machine';
import type { MachineCardData } from '../../shared/oem-engine';
import { MiniChart } from '../../shared/viz/MiniChart';
import { MachineThumb } from '../../shared/viz/Turntable';
import { engineHoursLabel, machineMetaLine, worstComponentLine } from './FleetHealthScene.transform';

/**
 * Demo line 2019. `DRV` — the drive-type accent the demo colours the card's
 * left edge with. Diesel pink is the machine livery colour, so it maps onto
 * the brand's livery token `--brand-secondary` — Meridian pink, Komatsu
 * yellow — matching the thumbnail's paint. Battery and autonomous keep their
 * own hues because they carry meaning independent of the brand.
 */
const DRIVE_EDGE: Readonly<Record<'diesel' | 'batt' | 'auto', string>> = {
  diesel: 'var(--brand-secondary)',
  batt: 'var(--band-good)',
  auto: 'var(--state-info)',
};

/** Demo line 2017. `drvOf` — battery, then autonomous, then diesel. */
const driveOf = (card: MachineCardData): 'diesel' | 'batt' | 'auto' =>
  card.machine.battery ? 'batt' : card.machine.autonomous ? 'auto' : 'diesel';

export interface MachineCardProps {
  card: MachineCardData;
}

/** Demo line 2131. `machineCard`. */
export function MachineCard({ card }: MachineCardProps) {
  const f = card.machine;
  const hasWorkings = Boolean(WORKINGS[f.mine]);
  const metric = card.metricInfo;
  const limitLine = metric.limit ? ` · limit ${metric.limit} ${metric.unit}` : '';

  return (
    <Link
      to={`/machines/${encodeURIComponent(f.machine_id)}`}
      title={`Open ${f.machine_id}`}
      className={cn(
        'group relative flex flex-col gap-1.5 rounded-xl border border-[var(--glass-border)]',
        'bg-[var(--glass-surface)] p-3 text-left transition-colors',
        'hover:bg-[var(--glass-surface-strong)]',
        'focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-[color-mix(in_srgb,var(--color-accent-500)_60%,transparent)]',
      )}
      style={{ borderLeft: `3px solid ${DRIVE_EDGE[driveOf(card)]}` }}
    >
      <span className="flex items-center justify-between gap-2">
        <span className="mono text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          {f.machine_id}
        </span>
        <span className={`band-pill band-${card.band}`}>{CONDITION_BAND_LABEL[card.band]}</span>
      </span>

      {/* Demo line 1150. `.rpic` — 74 × 34 at the top right, under the band pill. */}
      <MachineThumb
        machine={f}
        className="pointer-events-none absolute right-2 top-[26px] h-[34px] w-[74px] opacity-95"
      />

      <span className="flex flex-col gap-0.5 pr-[78px] text-[11px] leading-snug text-muted">
        <span className="truncate">{machineMetaLine(f, hasWorkings)}</span>
        <span className="mono">{engineHoursLabel(f)}</span>
        <span style={{ color: card.availabilityBand?.colorToken ?? 'inherit' }}>
          {`avail ${card.availability ?? '—'}%`}
          {card.availabilityBand ? ` · ${card.availabilityBand.label}` : ''}
        </span>
      </span>

      <span className="mt-1 flex items-baseline justify-between gap-2 text-[10.5px] text-muted">
        <span className="truncate">{`${metric.name} · ${metric.unit}${limitLine}`}</span>
        <b className="mono shrink-0 text-xs text-zinc-900 dark:text-zinc-100">
          {card.latest != null ? `${card.latest} ${metric.unit}` : '—'}
        </b>
      </span>

      <MiniChart
        values={card.values}
        dates={card.dates}
        unit={metric.unit}
        colour={`var(--band-${card.band})`}
        limit={metric.limit}
      />

      <span className="text-[11px] leading-snug text-muted">{worstComponentLine(card)}</span>
    </Link>
  );
}
